import React, { useEffect, useMemo, useState } from "react";
import { Select, Spin, Empty, Tag } from "antd";
import debounce from "lodash/debounce";

export interface CodeOption {
  label: string;
  value: string;
  code?: string; 
  realId?: string; 
  isActive?: boolean;
}

interface ClaimMultiSelectProps {
  fetchOptions: (searchTerm: string, page: number, pageSize: number) => Promise<{ data: CodeOption[]; total: number }>;
  placeholder?: string;
  disabled?: boolean;
  value: CodeOption[];
  onChange: (values: CodeOption[]) => void;
  style?: React.CSSProperties;
  minChars?: number; 
}

const PAGE_SIZE = 10;

const ClaimMultiSelect: React.FC<ClaimMultiSelectProps> = ({
  fetchOptions,
  placeholder,
  disabled = false,
  value,
  onChange,
  style,
  minChars = 3, 
}) => {
  const [options, setOptions] = useState<CodeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const canSearch = searchValue.length >= minChars;
  const dynamicPlaceholder = placeholder || `Type at least ${minChars} character${minChars > 1 ? 's' : ''}`;

  const loadOptions = async (val: string, pageNumber: number) => {
    setLoading(true);
    try {
      const res = await fetchOptions(val, pageNumber, PAGE_SIZE);
      setOptions(prev => pageNumber === 1 ? res.data : [...prev, ...res.data]);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useMemo(() => debounce((val: string) => {
    if (val.length < minChars) {
      setOptions([]);
      setTotal(0);
      return;
    }
    setPage(1);
    loadOptions(val, 1);
  }, 400), [fetchOptions, minChars]);

  useEffect(() => {
    debouncedSearch(searchValue);
    return () => debouncedSearch.cancel();
  }, [searchValue, debouncedSearch]);

  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (!loading && options.length < total && target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadOptions(searchValue, nextPage);
    }
  };

  return (
    <Select
      data-testid="ClaimMultiSelect-select"
      mode="multiple"
      showSearch
      allowClear
      placement="bottomLeft"
      disabled={disabled}
      labelInValue 
      value={value.map(v => ({ value: v.value, label: v.label }))}
      placeholder={canSearch ? "Search codes..." : dynamicPlaceholder}
      filterOption={false}
      options={options}
      optionLabelProp="label" 
      tagRender={({ label, value: valId, closable, onClose }) => {
        let fullOption = options.find(opt => opt.value === valId);
        
        if (!fullOption) {
          fullOption = value.find(v => v.value === valId);
        }
      
        const display = typeof label === 'string' ? label : (fullOption?.label || label);
        
        return (
          <Tag closable={closable} onClose={onClose} style={{ marginRight: 3 }}>
            {display}
          </Tag>
        );
      }}
      notFoundContent={loading ? <Spin size="small" /> : <Empty description="No results" />}
      onSearch={setSearchValue}
      onChange={(selectedValues: any[]) => {
        const singleSelection = selectedValues.slice(-1);

        onChange(
          singleSelection.map((v) => {
            const opt = options.find(o => o.value === v.value);
            return {
              value: v.value,
              label: v.label,
              code: opt?.code || (typeof v.label === 'string' ? v.label.split(' - ')[0] : ""),
              realId: opt?.realId || v.value
            };
          })
        );
      }}
      onPopupScroll={handlePopupScroll}
      loading={loading}
      listHeight={240}
      style={style}
      virtual={false}
      getPopupContainer={(trigger) => trigger.parentElement!}
    />
  );
};

export default ClaimMultiSelect;